import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { BaElsewhereNote } from '@platform/engine/insights/BaElsewhereNote';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { PageHeader } from '@platform/engine/list/header/PageHeader';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { useViewLabel } from '@platform/engine/list/useViewLabel';
import { modulePagesOf, pageLabel } from '@platform/engine/menu/modulePages';
import { isDesigned, tablesOf } from '@platform/engine/menu/moduleSections';
import type { IModulePageProps } from './IModulePageProps';
import './modulePages.css';

/** «Подання записів» довідки — сталий перелік прототипу (mhViews), незалежно від увімкнених подань модуля. */
const HELP_VIEWS = ['table', 'stages', 'cards', 'inline', 'backlog', 'split'] as const;

/**
 * Довідка модуля (прототип v4 isMHelp): крихти, назва модуля над «Довідка», «Як працює цей модуль…»; опис головної таблиці з акцентною
 * смугою, «Таблиці модуля» (реалізовані таблиці з описом — ведуть до таблиці; головна — назвою модуля, як mhTables прототипу), далі в сітці
 * «Етапи» (статуси через →), «Подання записів» (сталий перелік прототипу «Таблиця · Етапи · Картки · Редагування в рядку · Беклог ·
 * Розділений вигляд»; нижче — окремі аналітичні подання модуля, крім корових «Завдань» і «Бази знань») і «Сторінки модуля».
 */
export function ModuleHelpPage({ ws, module, rt }: IModulePageProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const viewName = useViewLabel(module);
  const moduleName = strings.moduleName(module.module.name);
  const crumbs = useModuleCrumbs(module.module.key, moduleName);
  const lead = strings.entity(rt.key, 'description', rt.description) || strings.tagline(module.module.tagline);
  const tables = tablesOf(module).filter((r) => !isDesigned(r) && !r.hidden);
  const stages = rt.statuses.list.map((s) => strings.status(rt.key, s.value, s.label));
  const views = HELP_VIEWS.map((v) => t(`modBody.help.viewNames.${v}`, { defaultValue: viewName(v) }));
  // Окремі аналітичні подання (VIEWS_BY_MOD прототипу) — лише некорових модулів: подання завдань і бази знань живуть у їхньому меню.
  const insights = module.module.core ? [] : modulePagesOf(module).filter((p) => p.kind === 'insight').map((p) => pageLabel(p, strings, (k) => t(`modulePages.${k}`)));
  return (
    <PageFrame crumbs={crumbs} head={<PageHeader eyebrow={moduleName} title={t('modulePages.help')} description={t('pageHead.helpDesc')} />}>
      <BaElsewhereNote ws={ws} moduleKey={module.module.key} rtKey={rt.key} />
      <div className="mp-help">
        {lead && <section className="mp-section mp-section--lead"><span className="mp-help__lead">{lead}</span></section>}
        <section className="mp-section">
          <div className="mp-section__label">{t('overview.tables')}</div>
          {tables.map((r) => {
            const description = strings.entity(r.key, 'description', r.description);
            return (
              <Link key={r.key} className="mp-help__table" to={routes.list(module.module.key, r.key, 'table')}>
                <strong>{r.key === rt.key ? moduleName : strings.entity(r.key, 'name', r.name)}</strong>
                {description && <span>{description}</span>}
              </Link>
            );
          })}
        </section>
        <div className="mp-help__grid">
          {stages.length > 0 && (
            <section className="mp-section"><div className="mp-section__label">{t('modBody.help.stages')}</div><span className="mp-help__text">{stages.join(' → ')}</span></section>
          )}
          <section className="mp-section">
            <div className="mp-section__label">{t('modBody.help.views')}</div>
            <span className="mp-help__text">{views.join(' · ')}</span>
            {insights.length > 0 && <span className="mp-help__note">{insights.join(' · ')}</span>}
          </section>
          <section className="mp-section"><div className="mp-section__label">{t('modBody.help.pages')}</div><span className="mp-help__pages">{t('modBody.help.pagesText')}</span></section>
        </div>
      </div>
    </PageFrame>
  );
}
